import type { Application, CandidateStructuredData, IdentityMismatch, IdentityMismatchField } from '@/types';

const UNKNOWN_IDENTITY_RE = /^<?\s*unknow(n)?\s*>?$/i;

/** AI / parser placeholders — treat as missing identity data. */
export function isUnknownIdentityValue(value: string | undefined | null): boolean {
  if (value == null) return true;
  const trimmed = value.trim();
  if (!trimmed) return true;
  return UNKNOWN_IDENTITY_RE.test(trimmed);
}

/** Display value for mismatch rows; missing/placeholder → "--". */
export function formatIdentityDisplayValue(value: string): string {
  return isUnknownIdentityValue(value) ? '--' : value.trim();
}

function cleanIdentityName(value: string): string {
  return collapseSpaces(
    value.split(/\s+/).filter((part) => !isUnknownIdentityValue(part)).join(' '),
  );
}

export function getDisplayableMismatchFields(
  fields: IdentityMismatchField[],
): IdentityMismatchField[] {
  return fields.filter((field) => {
    if (field.field === 'name') {
      const formName = cleanIdentityName(field.formValue);
      const resumeName = cleanIdentityName(field.resumeValue);
      return Boolean(formName && resumeName);
    }
    return (
      !isUnknownIdentityValue(field.formValue) &&
      !isUnknownIdentityValue(field.resumeValue)
    );
  });
}

/** Name mismatch = likely wrong person; contact-only = softer signal (typo/alternate email). */
export type IdentityMismatchSeverity = 'critical' | 'minor';

export function getIdentityMismatchSeverity(
  mismatch: Pick<IdentityMismatch, 'fields'>,
): IdentityMismatchSeverity | null {
  const displayFields = getDisplayableMismatchFields(mismatch.fields);
  if (displayFields.length === 0) return null;
  return displayFields.some((f) => f.field === 'name') ? 'critical' : 'minor';
}

export function formatMismatchFieldLine(
  field: IdentityMismatchField,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  const formValue =
    field.field === 'name'
      ? formatIdentityDisplayValue(cleanIdentityName(field.formValue))
      : formatIdentityDisplayValue(field.formValue);
  const resumeValue =
    field.field === 'name'
      ? formatIdentityDisplayValue(cleanIdentityName(field.resumeValue))
      : formatIdentityDisplayValue(field.resumeValue);

  return t('application.identityMismatch.fieldLine', {
    label: t(`application.identityMismatch.field.${field.field}`),
    formValue,
    resumeValue,
  });
}

function collapseSpaces(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

function foldAccents(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '');
}

function normalizeName(value: string): string {
  return foldAccents(collapseSpaces(value)).toLowerCase();
}

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

function normalizePhone(value: string): string {
  const digits = value.replace(/\D/g, '');
  return digits.length > 10 ? digits.slice(-10) : digits;
}

function extractLinkedInSlug(value: string): string {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return '';

  const inMatch = trimmed.match(/linkedin\.com\/in\/([^/?#]+)/);
  if (inMatch?.[1]) return inMatch[1].replace(/\/$/, '');

  if (/^[a-z0-9][a-z0-9-_]{2,99}$/.test(trimmed) && !trimmed.includes('.')) {
    return trimmed;
  }

  try {
    const url = trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;
    const parsed = new URL(url);
    const parts = parsed.pathname.split('/').filter(Boolean);
    const inIndex = parts.indexOf('in');
    if (inIndex >= 0 && parts[inIndex + 1]) {
      return parts[inIndex + 1]!.replace(/\/$/, '');
    }
  } catch {
    // fall through
  }

  return trimmed;
}

function isIdentityMismatch(value: unknown): value is IdentityMismatch {
  if (!value || typeof value !== 'object') return false;
  const v = value as IdentityMismatch;
  return v.hasMismatch === true && Array.isArray(v.fields);
}

function detectClientSideMismatch(
  app: Pick<Application, 'candidateFirstName' | 'candidateLastName' | 'candidateEmail' | 'candidatePhone'>,
  structured: CandidateStructuredData,
): IdentityMismatch | null {
  const fields: IdentityMismatchField[] = [];

  const formName = cleanIdentityName(`${app.candidateFirstName} ${app.candidateLastName}`);
  const resumeName = cleanIdentityName(`${structured.firstName ?? ''} ${structured.lastName ?? ''}`);
  if (formName && resumeName && normalizeName(formName) !== normalizeName(resumeName)) {
    fields.push({ field: 'name', formValue: formName, resumeValue: resumeName });
  }

  const formEmail = app.candidateEmail?.trim();
  const resumeEmail = structured.email?.trim();
  if (
    !isUnknownIdentityValue(formEmail) &&
    !isUnknownIdentityValue(resumeEmail) &&
    formEmail &&
    resumeEmail &&
    normalizeEmail(formEmail) !== normalizeEmail(resumeEmail)
  ) {
    fields.push({ field: 'email', formValue: formEmail, resumeValue: resumeEmail });
  }

  const formPhone = app.candidatePhone?.trim();
  const resumePhone = structured.phone?.trim();
  if (formPhone && resumePhone && !isUnknownIdentityValue(formPhone) && !isUnknownIdentityValue(resumePhone)) {
    const formDigits = normalizePhone(formPhone);
    const resumeDigits = normalizePhone(resumePhone);
    if (formDigits && resumeDigits && formDigits !== resumeDigits) {
      fields.push({ field: 'phone', formValue: formPhone, resumeValue: resumePhone });
    }
  }

  if (fields.length === 0) return null;

  const nameField = fields.find(f => f.field === 'name');
  const summary = nameField
    ? `Application form data (${nameField.formValue}) does not match the resume (${nameField.resumeValue}).`
    : 'Application form data does not match the resume.';

  return { hasMismatch: true, summary, fields };
}

export function resolveIdentityMismatch(
  app: Pick<
    Application,
    'candidateFirstName' | 'candidateLastName' | 'candidateEmail' | 'candidatePhone' | 'identityMismatch' | 'collectedData'
  >,
  structuredData?: CandidateStructuredData | null,
): IdentityMismatch | null {
  if (app.identityMismatch?.hasMismatch) {
    return app.identityMismatch;
  }

  const fromCollected = app.collectedData?.form_resume_identity_mismatch?.value;
  if (isIdentityMismatch(fromCollected)) {
    return fromCollected;
  }

  if (structuredData) {
    return detectClientSideMismatch(app, structuredData);
  }

  return null;
}
